// Public client-facing signing page for upload-type proposals.
//
// URL: /p/upload/:signer_token  — each signer has their own unique link.
//
// Flow: email gate → scrollable page sequence (PDF pages w/ image previews,
// video pages via iframe, signing pages w/ field layout) → only the fields
// assigned to THIS signer are editable → sign modal (type OR draw per
// signature field) → optional download of the fully-signed PDF once
// everyone has signed.

import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  CheckCircle2, Loader2, Lock, Mail, FileText, PenLine, Type as TypeIcon,
  Download, X, Clock, Ban,
} from "lucide-react";
import { VideoBlockRender } from "../components/VideoBlock";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const COL = { fill: "rgba(91,79,232,0.14)", stroke: "#5B4FE8" };

export default function PublicUploadProposal() {
  const { token } = useParams();
  const [email, setEmail] = useState("");
  const [state, setState] = useState({ loading: true, needsEmail: false, data: null, error: null });
  const [values, setValues] = useState({});
  const [sigModal, setSigModal] = useState(null); // {field_id}
  const [sigDataByField, setSigDataByField] = useState({}); // {fid: {mode, name?, image_b64?}}
  const [submitting, setSubmitting] = useState(false);
  const [showDecline, setShowDecline] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [declining, setDeclining] = useState(false);

  const load = async (em) => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const url = `${API}/public/upload-proposals/${token}${em ? `?email=${encodeURIComponent(em)}` : ""}`;
      const { data } = await axios.get(url);
      if (data.needs_email) {
        setState({ loading: false, needsEmail: true, data: null, error: null, tenantName: data.tenant_name, signerLabel: data.signer_label });
        return;
      }
      setState({ loading: false, needsEmail: false, data, error: null });
    } catch (e) {
      setState({ loading: false, needsEmail: false, data: null, error: e.response?.status === 404 ? "not_found" : "error" });
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  if (state.loading) {
    return <Shell><Card center><Loader2 className="animate-spin" size={22} style={{ color: "var(--pg-text-mute)" }} /></Card></Shell>;
  }
  if (state.error === "not_found") {
    return (
      <Shell>
        <Card center>
          <FileText size={28} style={{ color: "var(--pg-text-mute)", margin: "0 auto 10px" }} />
          <h2 style={{ fontSize: 18, fontWeight: 600 }}>Proposal not found</h2>
          <p className="text-sm mt-2" style={{ color: "var(--pg-text-mute)" }}>This link may have been unpublished or expired.</p>
        </Card>
      </Shell>
    );
  }
  if (state.needsEmail) {
    return (
      <Shell>
        <Card>
          <div style={{
            width: 44, height: 44, borderRadius: 12, margin: "0 auto 14px",
            background: "rgba(91,79,232,0.10)", color: "var(--pg-accent)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}><Lock size={20} /></div>
          <h2 style={{ fontSize: 17, fontWeight: 600, textAlign: "center" }}>Enter your email to continue</h2>
          <p className="text-sm mt-2 mb-5" style={{ color: "var(--pg-text-mute)", textAlign: "center" }}>
            {state.tenantName ? `${state.tenantName} shared a proposal with you.` : "A proposal was shared with you."}
            {state.signerLabel ? ` You've been invited as ${state.signerLabel}.` : ""}
            {" "}Your email helps us keep a record of who signed.
          </p>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="pg-input"
            placeholder="you@company.com"
            data-testid="public-upload-email-input"
            onKeyDown={(e) => { if (e.key === "Enter" && email.includes("@")) load(email); }}
          />
          <button
            className="pg-btn pg-btn-primary w-full mt-3"
            onClick={() => load(email)}
            disabled={!email.includes("@")}
            data-testid="public-upload-email-submit"
          >
            <Mail size={13} /> Open proposal
          </button>
        </Card>
      </Shell>
    );
  }

  const p = state.data.proposal;
  const signer = state.data.signer;
  const isAccepted = p.status === "accepted";
  const canSign = signer.can_sign && !isAccepted;
  const signedUrl = p.signed_pdf_file_id ? `${BACKEND}/api/public/files/${p.signed_pdf_file_id}` : null;
  const fieldsById = Object.fromEntries((p.fields || []).map(f => [f.id, f]));

  const requiredMissing = () => {
    for (const f of p.fields || []) {
      if (f.type === "text") continue;
      if (f.type === "signature") {
        if (!sigDataByField[f.id]) return true;
      } else if (f.required && !(values[f.id] || "").trim()) {
        return true;
      }
    }
    return false;
  };

  const submit = async () => {
    if (requiredMissing()) {
      alert("Please complete every required field marked with *");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        email,
        values,
        signatures: sigDataByField,
      };
      await axios.post(`${API}/public/upload-proposals/${token}/sign`, payload);
      await load(email);
    } catch (e) {
      const detail = e.response?.data?.detail;
      alert(typeof detail === "string" ? detail : "Could not submit — please try again");
    }
    setSubmitting(false);
  };

  const submitDecline = async () => {
    setDeclining(true);
    try {
      await axios.post(`${API}/public/upload-proposals/${token}/decline`, {
        email, reason: declineReason.trim() || null,
      });
      setShowDecline(false);
      await load(email);
    } catch (e) {
      const detail = e.response?.data?.detail;
      alert(typeof detail === "string" ? detail : "Could not submit — please try again");
    }
    setDeclining(false);
  };

  return (
    <Shell>
      <div style={{ maxWidth: 1080, margin: "0 auto" }}>
        <HeaderBar proposal={p} signer={signer} tenantName={state.data.tenant_name} />

        {/* Status banner */}
        {isAccepted ? (
          <Banner tone="success" testId="public-upload-status-accepted">
            <CheckCircle2 size={16} /> This proposal has been fully signed by all parties.
            {signedUrl && (
              <a href={signedUrl} target="_blank" rel="noreferrer" className="pg-btn pg-btn-primary" style={{ marginLeft: 12 }} data-testid="public-upload-download-signed">
                <Download size={13} /> Download signed PDF
              </a>
            )}
          </Banner>
        ) : p.status === "rejected" ? (
          <Banner tone="danger" testId="public-upload-status-rejected">
            <Ban size={16} /> This proposal has been declined and is no longer accepting signatures.
          </Banner>
        ) : signer.status === "declined" ? (
          <Banner tone="danger" testId="public-upload-status-declined">
            <Ban size={16} /> You've declined this proposal. The sender has been notified.
          </Banner>
        ) : signer.status === "signed" ? (
          <Banner tone="info" testId="public-upload-status-already-signed">
            <CheckCircle2 size={16} /> You've already signed. We'll email you the final PDF once all signers have completed their portion.
          </Banner>
        ) : !canSign ? (
          <Banner tone="info" testId="public-upload-status-waiting">
            <Clock size={16} /> Waiting on previous signers to complete their portion before you can sign.
          </Banner>
        ) : null}

        {/* Pages */}
        <div style={{ display: "flex", flexDirection: "column", gap: 24, marginTop: 20 }}>
          {(p.pages || []).map((page, idx) => (
            <PageViewer
              key={page.id}
              page={page}
              index={idx}
              fieldsOnPage={(p.fields || []).filter(f => f.page_id === page.id)}
              values={values}
              sigDataByField={sigDataByField}
              onValueChange={(fid, v) => setValues(vs => ({ ...vs, [fid]: v }))}
              onOpenSign={(fid) => setSigModal({ fieldId: fid })}
              editable={canSign}
            />
          ))}
        </div>

        {/* Submit footer */}
        {canSign && (
          <div style={{ position: "sticky", bottom: 0, marginTop: 32, padding: "16px 20px", background: "var(--pg-surface)", borderTop: "1px solid var(--pg-border)", borderRadius: "12px 12px 0 0", boxShadow: "0 -8px 24px rgba(0,0,0,0.08)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap" }} data-testid="public-upload-submit-bar">
            <div className="text-sm" style={{ color: "var(--pg-text-mute)" }}>
              Signing as <strong style={{ color: "var(--pg-text)" }}>{signer.label}</strong> · {email}
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <button
                className="pg-btn pg-btn-ghost"
                onClick={() => { setDeclineReason(""); setShowDecline(true); }}
                data-testid="public-upload-decline-btn"
                style={{ color: "var(--pg-red)" }}
              >
                <Ban size={13} /> Decline to sign
              </button>
              {(p.fields || []).length > 0 && (
                <button
                  className="pg-btn pg-btn-primary"
                  onClick={submit}
                  disabled={submitting}
                  data-testid="public-upload-submit"
                >
                  {submitting ? <Loader2 size={13} className="animate-spin" /> : <PenLine size={13} />}
                  {submitting ? "Submitting…" : "Finish signing"}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {sigModal && (
        <SignatureModal
          field={fieldsById[sigModal.fieldId]}
          initial={sigDataByField[sigModal.fieldId]}
          onCancel={() => setSigModal(null)}
          onConfirm={(data) => {
            setSigDataByField((d) => ({ ...d, [sigModal.fieldId]: data }));
            setSigModal(null);
          }}
        />
      )}
      {showDecline && (
        <DeclineModal
          reason={declineReason}
          setReason={setDeclineReason}
          onCancel={() => setShowDecline(false)}
          onConfirm={submitDecline}
          submitting={declining}
        />
      )}
    </Shell>
  );
}

// ---------------------------------------------------------------------------
// Headers, banners, shell
// ---------------------------------------------------------------------------
function HeaderBar({ proposal, signer, tenantName }) {
  const signersPublic = proposal.signers_public || [];
  return (
    <div className="pg-card" style={{ padding: 20, marginBottom: 14 }} data-testid="public-upload-header">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 700, margin: 0 }} data-testid="public-upload-title">{proposal.name}</h2>
          <div className="mono text-[11px] mt-1" style={{ color: "var(--pg-text-mute)" }}>
            {tenantName} · {proposal.number} · {signer.label}
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {signersPublic.map((s, i) => (
            <div
              key={s.id}
              style={{
                display: "flex", alignItems: "center", gap: 6,
                padding: "4px 10px", borderRadius: 999,
                background: s.id === signer.id ? "var(--pg-accent)" : "var(--pg-surface-2)",
                color: s.id === signer.id ? "white" : "var(--pg-text)",
                fontSize: 11, fontWeight: 500,
              }}
              data-testid={`public-upload-signer-chip-${i}`}
            >
              {s.status === "signed" ? <CheckCircle2 size={11} /> : <Clock size={11} />}
              {s.label}{s.name ? ` · ${s.name}` : ""}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Banner({ tone, children, testId }) {
  const palette = tone === "success"
    ? { bg: "rgba(22,163,74,0.08)", border: "rgba(22,163,74,0.3)", color: "#16a34a" }
    : tone === "danger"
    ? { bg: "rgba(220,38,38,0.08)", border: "rgba(220,38,38,0.3)", color: "#dc2626" }
    : { bg: "rgba(91,79,232,0.08)", border: "rgba(91,79,232,0.3)", color: "var(--pg-accent)" };
  return (
    <div style={{
      padding: 12, background: palette.bg, border: `1px solid ${palette.border}`,
      borderRadius: 10, display: "flex", alignItems: "center", gap: 10,
      fontSize: 13, color: palette.color, fontWeight: 500,
    }} data-testid={testId}>
      {children}
    </div>
  );
}

function Shell({ children }) {
  return (
    <div style={{ minHeight: "100vh", background: "var(--pg-bg)", padding: "40px 20px 20px" }}>
      {children}
    </div>
  );
}
function Card({ children, center }) {
  return (
    <div className="pg-card" style={{
      padding: 32, maxWidth: 460, margin: "0 auto",
      textAlign: center ? "center" : undefined,
    }}>{children}</div>
  );
}

// ---------------------------------------------------------------------------
// Page viewer — handles pdf_page / video / signing
// ---------------------------------------------------------------------------
function PageViewer({ page, index, fieldsOnPage, values, sigDataByField, onValueChange, onOpenSign, editable }) {
  if (page.type === "video") {
    return (
      <div data-testid={`public-page-video-${index}`}>
        <PageHeader label="Video" index={index} />
        <VideoBlockRender video={page.video || {}} />
      </div>
    );
  }

  let aspect = 0.77;
  if (page.type === "pdf_page") {
    const w = page.source_meta?.width || 612;
    const h = page.source_meta?.height || 792;
    aspect = w / h;
  }
  const bgImage = page.type === "pdf_page" && page.source_meta?.preview_file_id
    ? `url("${BACKEND}/api/public/files/${page.source_meta.preview_file_id}")` : null;

  return (
    <div data-testid={`public-page-${page.type}-${index}`}>
      <PageHeader label={page.type === "signing" ? (page.label || "Signatures & Client Details") : `Page ${(page.source_index ?? 0) + 1}`} index={index} />
      <div style={{
        position: "relative",
        aspectRatio: `${aspect}`,
        background: "white",
        backgroundImage: bgImage, backgroundSize: "100% 100%", backgroundRepeat: "no-repeat",
        borderRadius: 10, border: "1px solid var(--pg-border)",
        overflow: "hidden",
        boxShadow: "0 6px 16px rgba(0,0,0,0.08)",
      }}>
        {page.type === "signing" && (
          <div style={{
            position: "absolute", top: 0, left: 0, right: 0, height: "5%",
            background: "var(--pg-accent)", color: "white",
            padding: "0 16px", display: "flex", alignItems: "center",
            fontSize: 11, fontWeight: 600,
          }}>{page.label || "Signatures & Client Details"}</div>
        )}
        {fieldsOnPage.map((f) => {
          const val = values[f.id] || "";
          const sig = sigDataByField[f.id];
          const hasValue = f.type === "signature" ? !!sig : (f.type === "text" ? true : !!val);
          return (
            <div
              key={f.id}
              style={{
                position: "absolute",
                left: `${f.x * 100}%`, top: `${f.y * 100}%`,
                width: `${f.width * 100}%`, height: `${f.height * 100}%`,
                border: `1.5px dashed ${COL.stroke}`,
                background: hasValue ? "rgba(22,163,74,0.08)" : COL.fill,
                borderRadius: 4,
                padding: 3, boxSizing: "border-box",
                display: "flex", alignItems: "stretch",
              }}
              data-testid={`public-field-${f.id}`}
            >
              <PublicFieldInput
                field={f}
                value={val}
                signature={sig}
                onChange={(v) => onValueChange(f.id, v)}
                onOpenSign={() => onOpenSign(f.id)}
                editable={editable}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PageHeader({ label, index }) {
  return (
    <div className="mono text-[11px] mb-2" style={{ color: "var(--pg-text-mute)", letterSpacing: ".08em", textTransform: "uppercase" }}>
      Page {index + 1} · {label}
    </div>
  );
}

function PublicFieldInput({ field, value, signature, onChange, onOpenSign, editable }) {
  if (field.type === "text") {
    return (
      <div style={{ padding: 2, fontSize: Math.min(16, field.height * 400), color: "var(--pg-text)", overflow: "hidden", whiteSpace: "pre-wrap" }}>
        {field.value || field.label}
      </div>
    );
  }
  if (field.type === "signature") {
    const label = signature
      ? (signature.mode === "type" ? signature.name : "✓ Signature captured")
      : (field.label || "Click to sign");
    return (
      <button
        type="button"
        onClick={editable ? onOpenSign : undefined}
        disabled={!editable}
        style={{
          width: "100%", height: "100%", border: "none",
          background: "transparent", cursor: editable ? "pointer" : "not-allowed",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontFamily: signature?.mode === "type" ? "'Dancing Script', 'Brush Script MT', cursive" : undefined,
          fontStyle: signature?.mode === "type" ? "italic" : "normal",
          fontSize: signature?.mode === "type" ? Math.max(18, field.height * 300) : 12,
          color: signature ? "#0f172a" : "var(--pg-accent)",
          fontWeight: signature ? 400 : 600,
          overflow: "hidden",
        }}
        data-testid={`public-field-sign-${field.id}`}
      >
        {signature?.mode === "draw" && signature.image_b64 ? (
          <img src={signature.image_b64} alt="Signature" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
        ) : label}
      </button>
    );
  }
  const inputType = field.type === "client_email" ? "email" : (field.type === "date" ? "date" : "text");
  return (
    <input
      type={inputType}
      value={value}
      disabled={!editable}
      onChange={(e) => onChange(e.target.value)}
      placeholder={field.label}
      style={{
        width: "100%", height: "100%",
        border: "none", background: "transparent",
        padding: "0 4px", fontSize: Math.min(14, field.height * 260),
        color: "var(--pg-text)", outline: "none",
      }}
      required={field.required}
      data-testid={`public-field-input-${field.id}`}
    />
  );
}

// ---------------------------------------------------------------------------
// Signature modal (type/draw) — per-field
// ---------------------------------------------------------------------------
function SignatureModal({ field, initial, onCancel, onConfirm }) {
  const [mode, setMode] = useState(initial?.mode || "type");
  const [typedName, setTypedName] = useState(initial?.name || "");
  const [drawnPng, setDrawnPng] = useState(initial?.image_b64 || null);

  const canConfirm = mode === "type" ? !!typedName.trim() : !!drawnPng;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center"
      style={{ zIndex: 300, background: "rgba(15,23,42,0.6)" }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}
      data-testid="public-upload-sign-modal"
    >
      <div style={{
        width: 480, maxWidth: "92vw", background: "var(--pg-surface)",
        borderRadius: 14, border: "1px solid var(--pg-border)", padding: 22,
        boxShadow: "0 24px 60px rgba(0,0,0,0.3)",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600 }}>Sign: {field?.label || "Signature"}</h3>
          <button onClick={onCancel} className="pg-btn pg-btn-ghost" style={{ padding: 6 }} aria-label="Close"><X size={14} /></button>
        </div>
        <div style={{ display: "flex", gap: 6, marginBottom: 14, padding: 3, borderRadius: 999, background: "var(--pg-surface-2)", width: "fit-content" }}>
          <TabBtn active={mode === "type"} onClick={() => setMode("type")} testid="sign-mode-type"><TypeIcon size={13} /> Type</TabBtn>
          <TabBtn active={mode === "draw"} onClick={() => setMode("draw")} testid="sign-mode-draw"><PenLine size={13} /> Draw</TabBtn>
        </div>
        {mode === "type" ? (
          <>
            <label className="block text-[11px] mb-1" style={{ color: "var(--pg-text-mute)" }}>Your full name</label>
            <input className="pg-input" value={typedName} onChange={(e) => setTypedName(e.target.value)} placeholder="Jane Doe" data-testid="public-upload-type-name" autoFocus />
            <div className="mt-3 flex items-center justify-center" style={{ height: 110, borderRadius: 10, background: "var(--pg-surface-2)", border: "1px solid var(--pg-border)" }}>
              <span style={{ fontFamily: "'Dancing Script', 'Brush Script MT', cursive", fontStyle: "italic", fontSize: 36, color: "var(--pg-text)" }}>
                {typedName || "Preview appears here"}
              </span>
            </div>
          </>
        ) : (
          <DrawPad onChange={setDrawnPng} initial={drawnPng} />
        )}
        <div className="flex items-center justify-end gap-2 mt-5">
          <button onClick={onCancel} className="pg-btn pg-btn-ghost" data-testid="public-upload-sign-cancel">Cancel</button>
          <button
            onClick={() => onConfirm(mode === "type" ? { mode, name: typedName.trim() } : { mode, image_b64: drawnPng })}
            className="pg-btn pg-btn-primary"
            disabled={!canConfirm}
            data-testid="public-upload-sign-confirm"
          >Confirm</button>
        </div>
      </div>
    </div>
  );
}

function DeclineModal({ reason, setReason, onCancel, onConfirm, submitting }) {
  return (
    <div
      className="fixed inset-0 flex items-center justify-center"
      style={{ zIndex: 300, background: "rgba(15,23,42,0.6)" }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}
      data-testid="public-upload-decline-modal"
    >
      <div style={{
        width: 480, maxWidth: "92vw", background: "var(--pg-surface)",
        borderRadius: 14, border: "1px solid var(--pg-border)", padding: 22,
        boxShadow: "0 24px 60px rgba(0,0,0,0.3)",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
          <div>
            <h3 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>Decline to sign</h3>
            <p className="text-sm mt-1" style={{ color: "var(--pg-text-mute)", margin: "4px 0 0" }}>
              The sender will be notified and the proposal will be frozen. You can tell them why — it helps them follow up.
            </p>
          </div>
          <button onClick={onCancel} className="pg-btn pg-btn-ghost" style={{ padding: 6 }} aria-label="Close"><X size={14} /></button>
        </div>
        <label className="block text-[11px] mt-3 mb-1" style={{ color: "var(--pg-text-mute)" }}>Reason (optional)</label>
        <textarea
          className="pg-input w-full"
          rows={5}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Pricing isn't quite right · Need the SOW rescoped · etc."
          maxLength={1000}
          data-testid="public-upload-decline-reason"
          autoFocus
        />
        <div className="text-[11px] mt-1" style={{ color: "var(--pg-text-mute)" }}>{reason.length}/1000</div>
        <div className="flex items-center justify-end gap-2 mt-4">
          <button onClick={onCancel} className="pg-btn pg-btn-ghost" data-testid="public-upload-decline-cancel">Keep reviewing</button>
          <button
            onClick={onConfirm}
            disabled={submitting}
            className="pg-btn pg-btn-danger"
            style={{ background: "#dc2626", color: "white" }}
            data-testid="public-upload-decline-confirm"
          >
            {submitting ? "Submitting…" : "Decline proposal"}
          </button>
        </div>
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick} data-testid={testid}
      style={{
        display: "inline-flex", alignItems: "center", gap: 4,
        padding: "6px 12px", borderRadius: 999,
        background: active ? "var(--pg-surface)" : "transparent",
        color: active ? "var(--pg-text)" : "var(--pg-text-mute)",
        fontSize: 12, fontWeight: active ? 600 : 400, cursor: "pointer", border: "none",
      }}
    >{children}</button>
  );
}

function DrawPad({ onChange, initial }) {
  const ref = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const [hasInk, setHasInk] = useState(!!initial);

  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.lineCap = "round"; ctx.lineJoin = "round";
    ctx.lineWidth = 2.2; ctx.strokeStyle = "#0f172a";
    if (initial) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, c.width, c.height);
      img.src = initial;
    }
  }, [initial]);

  const rel = (e) => {
    const c = ref.current;
    const r = c.getBoundingClientRect();
    const pt = e.touches?.[0] || e;
    return { x: ((pt.clientX - r.left) / r.width) * c.width, y: ((pt.clientY - r.top) / r.height) * c.height };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; last.current = rel(e); };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = ref.current.getContext("2d");
    const p = rel(e);
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    setHasInk(true);
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    onChange(ref.current.toDataURL("image/png"));
  };
  const clear = () => {
    const c = ref.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    setHasInk(false); onChange(null);
  };

  return (
    <>
      <div style={{ position: "relative", borderRadius: 10, border: "1px solid var(--pg-border)", background: "white", overflow: "hidden" }}>
        <canvas
          ref={ref} width={620} height={200}
          data-testid="public-upload-draw-canvas"
          style={{ width: "100%", height: 200, touchAction: "none", cursor: "crosshair" }}
          onPointerDown={start} onPointerMove={move} onPointerUp={end} onPointerLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        />
        {!hasInk && (
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none", color: "#94a3b8", fontSize: 13 }}>
            Sign here with your mouse or finger
          </div>
        )}
      </div>
      <div className="flex items-center justify-between mt-2 text-xs" style={{ color: "var(--pg-text-mute)" }}>
        <span>Draw with your mouse, trackpad, or finger.</span>
        <button className="pg-btn pg-btn-ghost" onClick={clear} style={{ fontSize: 11 }} data-testid="public-upload-draw-clear">Clear</button>
      </div>
    </>
  );
}
